/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { i18n } from '../../../../translate/i18n';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TablePagination,
  TableRow,
  Typography,
  Avatar,
  Button,
  Box,
  Drawer,
  InputBase,
} from '@mui/material';
import * as actions from "../../../../store/actions";
import Spinner from '../../../../components/Spinner/index';
import EnhancedTableHead from "../../../../UI/MaterialTableOverrides/EnhancedTableHead";
import TablePaginationActionsWrapped from "../../../../UI/MaterialTableOverrides/TablePaginationActions";
import { renderStatusIcon, renderStatusColor } from '../../../../utils/statusManipulation';
import { RootState } from "../../../../store/types";
import TableFilters from "./TableFilters";
import FiltersModal from './FiltersModal';
import { IEquipment } from "../../../../models/iequipment";
import { Search } from "@mui/icons-material";

type Props = {
  setStep: CallableFunction,
}

const EquipmentsTable: React.FC<Props> = (props) => {
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [alreadySerched, setAlreadySerched] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [limit, setLimit] = useState(5);
  const [orderBy, setOrderBy] = useState("place");
  const [order, setOrder] = useState("asc");
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");

  const selectedSites = useSelector((state: any) => state.sites.selectedSitesPlaces);
  const filteredPlaces = useSelector((state: RootState) => state.createService.equipmentTableFilters.filteredPlaces);
  const filteredModels = useSelector((state: RootState) => state.createService.equipmentTableFilters.filteredModels);
  const filteredGroups = useSelector((state: RootState) => state.createService.equipmentTableFilters.filteredGroups);
  const filteredAreas = useSelector((state: RootState) => state.createService.equipmentTableFilters.filteredAreas);
  const filteredCompanies = useSelector((state: RootState) => state.createService.equipmentTableFilters.filteredCompanies);
  const filteredStatus = useSelector((state: RootState) => state.createService.equipmentTableFilters.filteredStatus);
  const equipments = useSelector((state: RootState) => state.createService.equipments);
  const equipmentsLoading = useSelector((state: RootState) => state.createService.equipmentsLoading);
 

  const fetchEquipments = useCallback(
    () => dispatch(actions.createServiceFetchEquipments(page, limit, searchText, order, orderBy, selectedSites, filteredPlaces, filteredModels, filteredGroups, filteredAreas, filteredCompanies, filteredStatus)),
    [page, limit, searchText, order, orderBy, selectedSites, filteredPlaces, filteredModels, filteredGroups, filteredAreas, filteredCompanies, filteredStatus]
  );

  const headRows = [
    { id: "equipmentModel.name", numeric: false, disablePadding: false, label: i18n.t('headRowsManagementEquip.equipmentModelName') },
    { id: "equipmentModel.equipmentGroup.name", numeric: false, disablePadding: false, label: i18n.t('headRowsManagementEquip.equipmentGroupName') },
    { id: "place.path", numeric: false, disablePadding: false, label: i18n.t('headRowsManagementEquip.placeName') },
    { id: "description", numeric: false, disablePadding: false, label: i18n.t('headRowsManagementEquip.description') },
    { id: "", numeric: false, disablePadding: false, label: "" , },
  ];

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setFirstLoad(false);
  }, []);

  useEffect(() => {
    if (!firstLoad) {
      fetchEquipments();
      setAlreadySerched(true);
    }
  }, [firstLoad, page, limit, order, orderBy]);

  useEffect(() => {
    if (!firstLoad) {
      setPage(1);
      fetchEquipments();
      setAlreadySerched(true);
    }
  }, [searchText, selectedSites, filteredPlaces, filteredModels, filteredGroups, filteredAreas, filteredCompanies, filteredStatus]);

  useEffect(() => {
    if (searchText !== "" || alreadySerched) {
      const timer = setTimeout(() => {
        if (searchText === inputRef.current?.value) {
          fetchEquipments();
        }
      }, 200);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [searchText, inputRef]);

  const handleChangePage = (e: any, page: number) => {
    setPage(page + 1);
  };

  const handleChangeRowsPerPage = (e: any) => {
    setLimit(e.target.value);
    setPage(1);
  };

  const handleRequestSort = (event: any, property: string) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const handleSelectEquipment = (equipment: IEquipment) => {
    dispatch(actions.createServiceSelectEquipment(equipment._id))
    props.setStep(1)
  }

  return <Box>
    <Drawer
      variant="temporary"
      anchor="right"
      onClose={() => setIsFiltersModalOpen(false)}
      open={isFiltersModalOpen}>
        <FiltersModal
          closeFiltersModal={() => setIsFiltersModalOpen(false)}
        />
    </Drawer>
    <Box style={sxs.searchRow}>
      <Search style={sxs.searchIcon} />
      <InputBase
        style={sxs.search}
        placeholder={i18n.t('tableTitleService.placeholder')}
        inputRef={inputRef}
        value={searchText}
        onChange={(event) => {setSearchText(event.target.value); setPage(1);}}
      />
    </Box>
    <Container maxWidth="lg" disableGutters>
      <TableFilters openFiltersModal={() => setIsFiltersModalOpen(true)} />
      <Table sx={sxs.table}>
        <tbody>
          <tr>
            <td>
              { equipmentsLoading &&
                <Box sx={sxs.spinner}>
                  <Spinner overlay={false} size={36}/>
                </Box>}
              {equipments.rows.length === 0 &&
                <Box sx={sxs.noResults}>
                  {!equipmentsLoading && <Typography sx={sxs.spinner}>{i18n.t('btnFilterManagementEquip.Typography') as string}</Typography>}
                </Box>}
            </td>
          </tr>
        </tbody>
        <EnhancedTableHead
          headRows={headRows}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
        />
        <TableBody sx={{ opacity: equipmentsLoading ? 0.3 : 1 }}>
          {equipments.rows && equipments.rows.map((equip) => {
            return (
              <TableRow
                sx={sxs.tableRow}
                key={equip._id}
                hover={false}
              >
                <TableCell sx={sxs.cell}>
                  <Box sx={sxs.cellPhotoContainer}>
                    <Box sx={sxs.status}>{renderStatusIcon(equip)}</Box>
                    {equip.photo ?
                      <Avatar
                        src={equip.photo.url}
                        sx={{
                          ...sxs.avatar,
                          border: `2px solid ${renderStatusColor(equip)}`
                        }}
                      />
                      :
                      <Avatar
                        sx={{
                          ...sxs.avatarSmallText,
                          border: `2px solid ${renderStatusColor(equip)}`
                        }}
                      >
                        FOTO
                      </Avatar>
                    }
                    <Box sx={sxs.equipmentName}>
                      <Box>{equip.equipmentModel.name}</Box>
                      <Box sx={{ color: 'grey' }}>{equip.serialNumber}</Box>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={sxs.cell}>{equip.equipmentModel.equipmentGroup && equip.equipmentModel.equipmentGroup.name}</TableCell>
                <TableCell sx={sxs.cell}>{equip.place && equip.place.path}</TableCell>
                <TableCell sx={sxs.cell}>{equip.description}</TableCell>
                <TableCell sx={sxs.cell}>
                  <Button
                    onClick={() => handleSelectEquipment(equip)}
                    variant="outlined"
                    color="primary"
                  >
                    {i18n.t('createService.select') as string}
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
        <TableFooter sx={{ opacity: equipmentsLoading ? 0.3 : 1 }}>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              count={equipments.count}
              rowsPerPage={limit}
              page={page - 1}
              SelectProps={{
                native: true
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActionsWrapped}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </Container>
  </Box>;

}

const sxs = {
  table: {
    position: 'relative',
    marginBottom: '50px',
  },
  tableRow: {
    cursor: 'pointer'
  },
  spinner: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    top: '-5px',
    opacity: '1',
    zIndex: 2,
  },
  noResults: {
    height: '100px',
  },
  deleteTextConfirm: {
    marginLeft: '-30px',
    fontWeight: 'bold',
  },
  editWidth: {
    width: '90%'
  },
  avatar: {
    width: '2.75rem',
    height: '2.75rem',
    marginLeft: '0.25rem',
  },
  avatarSmallText: {
    fontSize: '0.75rem',
    width: '2.75rem',
    height: '2.75rem',
    marginLeft: '0.25rem',
  },
  chip: {
    margin: 5,
  },
  thumbUp: {
    marginRight: '20px',
    color: "#349c34"
  },
  thumbDown: {
    marginRight: '20px',
    color: "#d04545"
  },
  cell: {
    fontSize: '0.775rem',
    padding: '0.25rem',
  },
  cellPhotoContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    marginLeft: '0.5rem',
  },
  buttonFilterContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0px 20px 10px 20px',
  },
  buttonFilterIcon: {
    marginLeft: 5
  },
  buttonFilter: {
    zIndex: 200,
    backgroundColor: 'green',
    height: '2.2rem',
    width: '6.5rem',
    padding: '1rem',
    color: '#fff',
  },
  clearFilter: {
    cursor: 'pointer',
    textDecoration: 'underline',
    zIndex: 200,
    padding: '0.75rem 0.5rem',
    color: '#000',
  },
  filterContainer: {
    position: 'relative',
    width: '100%',
  },
  filterChipContainer: {
    paddingLeft: 20,
    display: 'flex',
    flexWrap: 'wrap',
  },
  filterChip: {
    cursor: 'pointer',
    margin: 5,
  },
  equipmentName: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '0.75rem',
  },
	status: {
		marginRight: '0.25rem'
	},
  searchRow: {
    marginTop: '-24px',
    marginBottom: '24px',
    maxWidth: '500px',
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #ccc',
    padding: '2px 4px',
    borderRadius: '5px',
  },
  searchIcon: {
    color: '#ccc'
  },
  search: {
    flex: 1,
    marginLeft: '8px',
  },
}

export default EquipmentsTable;



